import {
  remult,
  repo,
  SqlDatabase,
  type EntityFilter,
  type PreprocessFilterEvent,
} from "remult";
import {
  EntityHyperFilter,
  type RelationalOrJsonEntityFilter,
} from "../../helpers/sql-relations";

import { type BaseRoles } from "@shared/Roles";
import { awaiter } from "@shared/helpers/awaiter";

/**
 * Represents a condition for filtering entities. This can be:
 * - `boolean`: `true` for full access (no additional filter), `false` for no access (no records). 🚫
 * - `EntityFilter<Entity>`: A standard Remult filter object. ✨
 * - `RelationalOrJsonEntityFilter<Entity>`: An `HyperFilter` object that can include
 * relational conditions or JSON path queries. 🔗
 * - `(RelationalOrJsonEntityFilter<Entity> | EntityFilter<Entity>)[]`: An array of conditions,
 * which will be combined with a logical `AND`. ➕
 *
 * @template Entity The type of the Remult entity.
 */
export type Check<Entity> =
  | boolean
  | (() => boolean | Promise<boolean>)
  | RelationalOrJsonEntityFilter<Entity>
  | EntityFilter<Entity>
  | (RelationalOrJsonEntityFilter<Entity> | EntityFilter<Entity>)[];

/**
 * A callback function used within `preProcessFilterPerRoles` to define
 * access conditions based on user roles. 🧑‍🤝‍🧑
 *
 * This function is overloaded to accept single roles or arrays of roles,
 * and single conditions or arrays of conditions.
 *
 * @template Entity The type of the Remult entity.
 */
export interface RoleCheck<Entity> {
  /**
   * Defines a filter condition for a single role.
   * @param role The role to apply the condition to.
   * @param condition The filter condition (if multiple conditions are provided -> they are joined with an `AND` logical operator).
   */
  (role: BaseRoles, condition: Check<Entity>): void;
  /**
   * Defines a filter condition for multiple roles. If the user has *any* of these roles,
   * the condition will be applied.
   * @param roles An array of roles.
   * @param condition The filter condition (if multiple conditions are provided -> they are joined with an `AND` logical operator).
   */
  (roles: BaseRoles[], condition: Check<Entity>): void;
}

/**
 * A powerful utility function designed to create a `apiPreprocessFilter` for Remult entities,
 * enabling flexible role-based access control (RBAC) on your API data. 🔐
 *
 * This function allows you to define what data different user roles can see,
 * by combining conditions with logical `OR` (across roles) and `AND` (within a role's conditions).
 *
 * @template Entity The type of the Remult entity.
 * @param roleChecks A callback function where you define your role-based access rules
 * using the provided `filter` (RoleCheck) function. This function can be `async`.
 * @param userRoles Optional. The roles of the current user. Defaults to `remult.user?.roles`.
 * You can override this for testing or specific contexts.
 * @returns An asynchronous function suitable for use as `apiPreprocessFilter` in a Remult `@Entity` decorator.
 *
 * @example
 * ```typescript
 * // In your entity definition (e.g., src/shared/entities/Order.ts)
 * import { Entity, Fields, remult } from "remult";
 * import { preProcessFilterPerRoles } from "@shared/helpers/preprocess-filter-for-role";
 * import { Roles } from "@shared/Roles";
 *
 * @Entity<Order>("orders", {
 * apiPreprocessFilter: preProcessFilterPerRoles((filter) => {
 * // 🚀 SuperAdmins can see EVERYTHING!
 * filter(Roles.SuperAdmins, true);
 *
 * // 🏢 Admins and Managers can see orders within their own organization.
 * filter(Roles.AdminsAndManager, {
 * organization: {
 *    $is: {
 *      managers: {
 *        $some: {
 *          id: remult.user?.id
 *        }
 *      }
 *    }
 *  }
 * });
 *
 * // 👤 Regular Users can only see orders they have placed.
 * filter(Roles.User, { userId: remult.user?.id });
 *
 * // ✍️ Requesters can see orders they've created OR orders assigned to them.
 * filter(Roles.Requester, [
 * { requester: { $id: remult.user?.id } },    // Orders they initiated
 * { assignedToUser: { $id: remult.user?.id } }, // Orders assigned to them
 * ]);
 *
 * // 🕵️ managers can also see all orders with 'status: "pending", even if it's not in their organization.
 * filter(Roles.Manager, { status: "pending" });
 *
 * // You might still use `allowApiCrud` for general permissions,
 * // but `apiPreprocessFilter` will refine row-level access.
 * allowApiCrud: Roles.All,
 * })
 * export class Order {
 *  ...
 * }
 * ```
 */
export function preProcessFilterPerRoles<Entity>(
  roleChecks: (filter: RoleCheck<Entity>) => void | Promise<void>,
  options: {
    userRoles?: string[];
    disableDevelopmentSecurityLogger?: boolean;
  } = {},
) {
  const {
    userRoles = remult.user?.roles,
    disableDevelopmentSecurityLogger = false,
  } = options;
  return async function (
    fixedFilter: EntityFilter<Entity>,
    event: PreprocessFilterEvent<Entity>,
  ): Promise<EntityFilter<Entity>> {

    const entity = event.metadata.entityType; // Get the entity type for filterWithRelationsAndJson

    const falseFilter: EntityFilter<Entity> =
      event.metadata.idMetadata.createIdInFilter([]);

    const trueFilter: EntityFilter<Entity> = {};

    // If no user roles are present, return an empty filter, effectively denying access.
    if (!userRoles || userRoles.length === 0) {
      console.warn(
        "No user roles found. Denying access to entity:",
        entity.name,
      );
      return falseFilter;
    }

    /**
     * Internal helper to convert various `Check` types into a Remult `EntityFilter`.
     * @param check The condition to convert.
     * @returns A Remult `EntityFilter`.
     */
    async function conditionToEntityFilter(
      check: Check<Entity>,
    ): Promise<EntityFilter<Entity>> {
      if (Array.isArray(check)) {
        // If it's an array of checks, combine them with an `$and` operator.
        return {
          $and: await Promise.all(check.map((v) => conditionToEntityFilter(v))),
        } as EntityFilter<Entity>;
      }
      if (typeof check === "boolean") {
        // Convert boolean `true` to `1=1` (always true) and `false` to `1=0` (always false)
        // for robust SQL compatibility.
        return check ? trueFilter : falseFilter;
      } else if (check instanceof Function) {
        const result = await check();
        return result ? trueFilter : falseFilter;
      } else {
        // Use the external `filterWithRelationsAndJson` for complex filters.
        return EntityHyperFilter(
          entity,
          check as RelationalOrJsonEntityFilter<Entity>,
        );
      }
    }

    const orFilters: EntityFilter<Entity>[] = [];
    const pendingTasks: Promise<void>[] = [];

    /**
     * The `filter` function provided to the `roleChecks` callback.
     * This is where developers define their role-based conditions.
     * It accumulates conditions into `orFilters` if the user matches the specified roles.
     *
     * @param roles The role(s) for which the condition applies. Can be a single `BaseRoles` or an array.
     * @param conditions The filter condition(s) to apply if the user has the role(s).
     */
    const filter: RoleCheck<Entity> = (roles, conditions) => {
      const task = new Promise<void>(async (resolve) => {
        const rolesArr = Array.isArray(roles) ? roles : [roles];
        const conditionsArr = Array.isArray(conditions)
          ? conditions
          : [conditions];

        // Check if the user has *any* of the roles specified in `rolesArr`.
        const userHasMatchingRole = userRoles.some((role) =>
          rolesArr.includes(role as BaseRoles),
        );

        if (!userHasMatchingRole) {
          return resolve(); // User doesn't have the required role(s), so skip this condition.
        }

        // If the user has one of the given roles, apply the associated filters.
        // Multiple conditions for a single role are combined with `$and`.
        const andFilters: EntityFilter<Entity>[] = await Promise.all(
          conditionsArr.map(conditionToEntityFilter),
        );

        orFilters.push({
          $and: andFilters,
        } as EntityFilter<Entity>);

        resolve();
      });

      pendingTasks.push(task);
    };

    // Execute the developer-defined role checks.
    await roleChecks(filter);

    // Execute pending tasks
    await Promise.all(pendingTasks);

    // Combine the client's original filter (`fixedFilter`) with the
    // role-based filters (`orFilters`).
    // If `orFilters` is empty, the `$or` will evaluate to `1=0`, effectively
    // denying access if no role-based conditions were met.
    const finalFilter = {
      $and: [
        fixedFilter, // The filter passed by the client (e.g., query params)
        orFilters.length === 0
          ? falseFilter
          : {
            $or: orFilters, // The filters derived from the user's roles
          },
      ],
    } as EntityFilter<Entity>;

    if (
      !disableDevelopmentSecurityLogger &&
      process.env.MODE === "development"
    ) {
      // If we are in development - log to console if anything was filtered out
      const entityRepo = repo(entity);
      const [originalResult, processedResult] = await awaiter(
        entityRepo.find({
          where: fixedFilter,
        }),
        entityRepo.find({
          where: finalFilter,
        }),
      );

      let isDifferent = false;

      if (originalResult.length !== processedResult.length) {
        isDifferent = true;
      } else {
        for (let i = 0; i < originalResult.length; i++) {
          const originalValue = originalResult[i];
          const processedValue = processedResult.find(
            (v) =>
              event.metadata.idMetadata.getId(v) ===
              event.metadata.idMetadata.getId(originalValue),
          );

          if (!processedValue) {
            isDifferent = true;
            break;
          }
        }
      }

      if (isDifferent) {
        console.error(
          `[Entity Security Mismatch] "${event.metadata.dbName}": filtered ${originalResult.length - processedResult.length} record(s).`,
          {
            originalFilter: await SqlDatabase.filterToRaw(entity, fixedFilter),
            processedFilter: await SqlDatabase.filterToRaw(entity, finalFilter),
          },
        );
      }
    }

    return finalFilter;
  };
}
